
import CurrencyInput from "@/components/widgets/CurrencyInput.vue";
import Page from "@/layout/Page.vue";
import {
  AuthModule,
  Justificacion2025Module,
  OrganismosModule,
} from "@/store/modules";
import { Justificacion2025 } from "@/store/modules/Justificacion2025Module";
import { ElNotification } from "element-plus";
import Swal from "sweetalert2";
import { computed, defineComponent, nextTick, onBeforeMount, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";

interface CreditoNoEjecutado {
  l1: number;
  l2: number;
  l3: number;
  l4: number;
  l5: number;
}

export default defineComponent({
  name: "Justificacion2025",
  components: {
    Page,
    CurrencyInput,
  },
  setup() {
    const { t } = useI18n();
    const store = useStore();
    const authModule = getModule(AuthModule, store);
    const justificacionModule = getModule(Justificacion2025Module, store);
    const organismosModule = getModule(OrganismosModule, store);

    const userComarcaId = authModule.user.comarcaId;

    // Refs individuales para cada campo
    const l1CreditoComprometido = ref<number | null>(null);
    const l1CreditoEjecutado = ref<number | null>(null);
    const l2CreditoComprometido = ref<number | null>(null);
    const l2CreditoEjecutado = ref<number | null>(null);
    const l3CreditoComprometido = ref<number | null>(null);
    const l3CreditoEjecutado = ref<number | null>(null);
    const l4CreditoComprometido = ref<number | null>(null);
    const l4CreditoEjecutado = ref<number | null>(null);
    const l5CreditoComprometido = ref<number | null>(null);
    const l5CreditoEjecutado = ref<number | null>(null);

    const isLoading = ref<boolean>(false);
    const dataLoadKey = ref<number>(0); // Para forzar re-render de CurrencyInput

    const currencyOptions = {
      currency: "EUR",
      locale: "es-ES",
      currencyDisplay: "symbol",
      precision: 2,
      hideCurrencySymbolOnFocus: true,
      hideGroupingSeparatorOnFocus: true,
      hideNegligibleDecimalDigitsOnFocus: true,
      autoDecimalDigits: false,
      useGrouping: true,
      accountingSign: false,
    };

    const comarcaNombre = computed(() => {
      const comarca = organismosModule.organismosListado.find(
        (o) => o.id === userComarcaId
      );
      return comarca?.nombreCompuesto || userComarcaId || "Comarca";
    });

    const creditoNoEjecutado = computed((): CreditoNoEjecutado => {
      return {
        l1:
          (l1CreditoComprometido.value || 0) - (l1CreditoEjecutado.value || 0),
        l2:
          (l2CreditoComprometido.value || 0) - (l2CreditoEjecutado.value || 0),
        l3:
          (l3CreditoComprometido.value || 0) - (l3CreditoEjecutado.value || 0),
        l4:
          (l4CreditoComprometido.value || 0) - (l4CreditoEjecutado.value || 0),
        l5:
          (l5CreditoComprometido.value || 0) - (l5CreditoEjecutado.value || 0),
      };
    });

    const totalComprometido = computed((): number => {
      return (
        (l1CreditoComprometido.value || 0) +
        (l2CreditoComprometido.value || 0) +
        (l3CreditoComprometido.value || 0) +
        (l4CreditoComprometido.value || 0) +
        (l5CreditoComprometido.value || 0)
      );
    });

    const totalEjecutado = computed((): number => {
      return (
        (l1CreditoEjecutado.value || 0) +
        (l2CreditoEjecutado.value || 0) +
        (l3CreditoEjecutado.value || 0) +
        (l4CreditoEjecutado.value || 0) +
        (l5CreditoEjecutado.value || 0)
      );
    });

    const totalComprometidoNoEjecutado = computed((): number => {
      return (
        creditoNoEjecutado.value.l1 +
        creditoNoEjecutado.value.l2 +
        creditoNoEjecutado.value.l3 +
        creditoNoEjecutado.value.l4 +
        creditoNoEjecutado.value.l5
      );
    });

    const formatCurrency = (value: number): string => {
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const loadData = async () => {
      try {
        isLoading.value = true;
        const data = await justificacionModule.fetchByComarcaId(userComarcaId);
        console.log("Data received from backend:", data);

        if (data) {
          l1CreditoComprometido.value = data.l1CreditoComprometido;
          l1CreditoEjecutado.value = data.l1CreditoEjecutado;
          l2CreditoComprometido.value = data.l2CreditoComprometido;
          l2CreditoEjecutado.value = data.l2CreditoEjecutado;
          l3CreditoComprometido.value = data.l3CreditoComprometido;
          l3CreditoEjecutado.value = data.l3CreditoEjecutado;
          l4CreditoComprometido.value = data.l4CreditoComprometido;
          l4CreditoEjecutado.value = data.l4CreditoEjecutado;
          l5CreditoComprometido.value = data.l5CreditoComprometido;
          l5CreditoEjecutado.value = data.l5CreditoEjecutado;

          console.log("Refs updated:", {
            l1Comprometido: l1CreditoComprometido.value,
            l1Ejecutado: l1CreditoEjecutado.value,
            l2Comprometido: l2CreditoComprometido.value,
            l2Ejecutado: l2CreditoEjecutado.value,
          });

          // Forzar reinicialización de CurrencyInput cambiando la clave
          await nextTick();
          dataLoadKey.value++;
        }
      } catch (error) {
        console.error("Error loading justification data:", error);
      } finally {
        isLoading.value = false;
      }
    };

    const onSubmit = async () => {
      try {
        isLoading.value = true;

        const payload: Justificacion2025 = {
          l1CreditoComprometido: l1CreditoComprometido.value || 0,
          l1CreditoEjecutado: l1CreditoEjecutado.value || 0,
          l2CreditoComprometido: l2CreditoComprometido.value || 0,
          l2CreditoEjecutado: l2CreditoEjecutado.value || 0,
          l3CreditoComprometido: l3CreditoComprometido.value || 0,
          l3CreditoEjecutado: l3CreditoEjecutado.value || 0,
          l4CreditoComprometido: l4CreditoComprometido.value || 0,
          l4CreditoEjecutado: l4CreditoEjecutado.value || 0,
          l5CreditoComprometido: l5CreditoComprometido.value || 0,
          l5CreditoEjecutado: l5CreditoEjecutado.value || 0,
          comarcaId: userComarcaId,
        };

        if (justificacionModule.isNew) {
          await justificacionModule.create({
            comarcaId: userComarcaId,
            justificacion: payload,
          });
        } else {
          await justificacionModule.update({
            comarcaId: userComarcaId,
            justificacion: payload,
          });
        }

        ElNotification({
          message: t("justificacion2025.mensaje.exito"),
          type: "success",
        });
      } catch (error) {
        console.error("Error saving justification:", error);
        ElNotification({
          message: t("justificacion2025.mensaje.error"),
          type: "error",
        });
      } finally {
        isLoading.value = false;
      }
    };

    onBeforeMount(async () => {
      try {
        await organismosModule.fetchAll();
        await loadData();
      } catch (error) {
        console.error("Error initializing component:", error);
        Swal.fire({
          text:
            t("formularios.justificacion2025.cargar.error") ||
            "Error al cargar los datos",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      }
    });

    return {
      l1CreditoComprometido,
      l1CreditoEjecutado,
      l2CreditoComprometido,
      l2CreditoEjecutado,
      l3CreditoComprometido,
      l3CreditoEjecutado,
      l4CreditoComprometido,
      l4CreditoEjecutado,
      l5CreditoComprometido,
      l5CreditoEjecutado,
      isLoading,
      currencyOptions,
      creditoNoEjecutado,
      totalComprometido,
      totalEjecutado,
      totalComprometidoNoEjecutado,
      formatCurrency,
      onSubmit,
      comarcaNombre,
      dataLoadKey,
    };
  },
});
